import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../../services/apiServices";

// Async Thunks

// Fetch barcode templates
export const fetchTemplates = createAsyncThunk(
  "barcodeTemplate/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get("barcode-settings");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching templates");
    }
  }
);

// Create a new barcode template
export const createTemplate = createAsyncThunk(
  "barcodeTemplate/createTemplate",
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await apiServices.post("barcode-settings", templateData);
      return response.data.template;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating template");
    }
  }
);

// Update a barcode template
export const updateTemplate = createAsyncThunk(
  "barcodeTemplate/updateTemplate",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`barcode-settings/${id}`, updatedData);
      return response.data.template;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating template");
    }
  }
);

// Delete a barcode template
export const deleteTemplate = createAsyncThunk(
  "barcodeTemplate/deleteTemplate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiServices.delete(`barcode-settings/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting template");
    }
  }
);

const barcodeTemplateSlice = createSlice({
  name: "barcodeTemplate",
  initialState: {
    templates: [],
    status: "idle", // "idle" | "loading" | "succeeded" | "failed"
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchTemplates
      .addCase(fetchTemplates.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.templates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle createTemplate
      .addCase(createTemplate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.templates.push(action.payload);
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle updateTemplate
      .addCase(updateTemplate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.templates.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle deleteTemplate
      .addCase(deleteTemplate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.templates = state.templates.filter((t) => t.id !== action.meta.arg);
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError } = barcodeTemplateSlice.actions;

export default barcodeTemplateSlice.reducer;
